import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  Fragment,
  useMemo,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'src/components/buttons-and-actions/button';
import { assertContext } from 'src/utils/assert-context';

type WithinModalContextValue = {
  close: (() => void) | undefined;
};

const WithinModalContext = createContext<WithinModalContextValue | undefined>(
  undefined
);

type WithinModalContextProviderProps = {
  value: WithinModalContextValue;
  children: ReactNode;
};

export function WithinModalContextProvider({
  value,
  children,
}: WithinModalContextProviderProps) {
  return (
    <WithinModalContext.Provider value={value}>
      {children}
    </WithinModalContext.Provider>
  );
}

export function useWithinModal() {
  return useContext(WithinModalContext);
}

type ModalEntry = {
  id: string;
  /** Invoked on every portal render so parent closures stay fresh. */
  render: () => ReactNode;
};

type ModalContainerActions = {
  add: (entry: ModalEntry) => void;
  close: (id: string) => void;
  /** Re-render the portal for an open modal without changing children. */
  refresh: (id: string) => void;
  /** Sync read of whether `id` is currently in the portal. */
  isOpen: (id: string) => boolean;
};

const ModalContainerContext = createContext<ModalContainerActions>(
  undefined! // assert context at runtime
);

function useModalContainer() {
  const value = useContext(ModalContainerContext);
  assertContext(value, 'ModalContainer');
  return value;
}

function ModalPortal({ modals }: { modals: ModalEntry[] }) {
  if (modals.length === 0) return null;
  return createPortal(
    modals.map((modal) => <Fragment key={modal.id}>{modal.render()}</Fragment>),
    document.body
  );
}

export const ModalContainerProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [modals, setModals] = useState<ModalEntry[]>([]);
  const modalsRef = useRef(modals);
  modalsRef.current = modals;

  const add = useCallback((entry: ModalEntry) => {
    setModals((current) => [
      ...current.filter((modal) => modal.id !== entry.id),
      entry,
    ]);
  }, []);

  const close = useCallback((id: string) => {
    setModals((current) => {
      const next = current.filter((modal) => modal.id !== id);
      return next.length === current.length ? current : next;
    });
  }, []);

  const refresh = useCallback((id: string) => {
    setModals((current) => {
      if (!current.some((modal) => modal.id === id)) return current;
      // New array identity so the portal re-renders and re-calls render().
      return [...current];
    });
  }, []);

  const isOpen = useCallback((id: string) => {
    return modalsRef.current.some((modal) => modal.id === id);
  }, []);

  const actions = useMemo(
    () => ({ add, close, refresh, isOpen }),
    [add, close, refresh, isOpen]
  );

  return (
    <ModalContainerContext.Provider value={actions}>
      {children}
      <ModalPortal modals={modals} />
    </ModalContainerContext.Provider>
  );
};

type ModalHookReturn<Params extends unknown[]> = {
  open: (...params: Params) => void;
  close: () => void;
};

export type ModalApi = {
  close: () => void;
  closeButton: ReactNode;
};

// `any[]` on the constraint keeps typed `open(...)` params assignable (variance).
type ModalCallback<Params extends any[] = any[]> = (
  props: ModalApi,
  ...params: Params
) => ReactNode;

type InferModalParams<Callback> = Callback extends (
  props: ModalApi,
  ...params: infer Params
) => ReactNode
  ? Params
  : [];

type UseModalReturn<Callback> = Callback extends null
  ? null
  : ModalHookReturn<InferModalParams<Callback>>;

/**
 * @param callback A function that renders the modal. It receives the modal API
 * as the first argument and optionally any arguments passed to `open` afterward.
 *
 * Pass `null` when the modal is unavailable. The hook will then return `null` as well.
 */
export function useModal<Callback extends ModalCallback | null>(
  callback: Callback
): UseModalReturn<Callback> {
  const id = useId();
  const modalContainer = useModalContainer();

  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const paramsRef = useRef<unknown[]>([]);

  const close = useCallback(() => {
    modalContainer.close(id);
  }, [id, modalContainer]);

  const open = useCallback(
    (...params: unknown[]) => {
      paramsRef.current = params;
      modalContainer.add({
        id,
        render: () =>
          callbackRef.current?.(
            {
              close,
              closeButton: (
                <Button
                  mode="secondary"
                  data-testid="close-modal"
                  onClick={close}
                >
                  Close
                </Button>
              ),
            },
            // Generic body cannot prove Params; runtime matches the callback.
            ...(paramsRef.current as any[])
          ),
      });
    },
    [close, id, modalContainer]
  );

  // Parent re-rendered while open → re-run the callback with fresh closures,
  // keeping params captured at the last open().
  // Not having a dependency array is intentional. Note that a re-render in
  // the parent should only re-render the modal, not the whole app. That way it would
  // behave like a component that would be rendered directly in the parent and not a portal.
  useLayoutEffect(() => {
    if (modalContainer.isOpen(id)) {
      modalContainer.refresh(id);
    }
  });

  useEffect(() => {
    return () => {
      if (modalContainer.isOpen(id)) {
        modalContainer.close(id);
      }
    };
  }, [id, modalContainer]);

  // whenever callback switches to null, close the modal, if it is still open
  useEffect(() => {
    if (!callback && modalContainer.isOpen(id)) {
      modalContainer.close(id);
    }
  }, [callback, id, modalContainer]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (callback ? value : null) as UseModalReturn<Callback>;
}
